import React, { useState } from 'react';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert } from '@mui/material';
import { Message as MessageIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../utils/api';

const API_BASE = API_BASE_URL;

interface MessageButtonProps {
  receiverId: number;
  receiverName: string;
  variant?: 'contained' | 'outlined' | 'text';
  size?: 'small' | 'medium' | 'large';
  startIcon?: boolean;
}

const MessageButton: React.FC<MessageButtonProps> = ({
  receiverId,
  receiverName,
  variant = 'contained',
  size = 'small',
  startIcon = true
}) => {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const handleSendMessage = async () => {
    if (!message.trim()) return;

    setLoading(true);
    setAlert(null);

    try {
      const res = await fetch(`${API_BASE}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          receiverId,
          content: message.trim()
        })
      });

      if (res.ok) {
        setAlert({ type: 'success', message: 'Message sent successfully!' });
        setMessage('');
        setTimeout(() => {
          setOpen(false);
          setAlert(null);
        }, 1500);
      } else if (res.status === 401) {
        window.location.href = '/login';
      } else {
        const errorData = await res.json().catch(() => ({}));
        setAlert({ type: 'error', message: errorData.error || 'Failed to send message' });
      }
    } catch (error) {
      setAlert({ type: 'error', message: 'Failed to send message' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    if (!token) {
      window.location.href = '/login';
      return;
    }
    setOpen(true);
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        startIcon={startIcon ? <MessageIcon /> : undefined}
        onClick={handleOpen}
        disabled={!token}
      >
        Message
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Send Message to {receiverName}
        </DialogTitle>
        <DialogContent>
          {alert && (
            <Alert severity={alert.type} sx={{ mb: 2 }}>
              {alert.message}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Message"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type your message here..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSendMessage}
            variant="contained"
            disabled={!message.trim() || loading}
          >
            {loading ? 'Sending...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MessageButton;


